import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IStaff } from '../../../models/employees.model';
import { IHousing } from '../../../models/employeesHousing.model';
import { doGetHousing, doCreateHousing, doUpdateHousing } from '../../../store/housing.slice';
import { doRemoveAuth } from '../../../store/auth.slice';
import { RootState } from '../../../store/rootReducer';
import moment from 'moment';

import { CgCloseR } from 'react-icons/cg';
import Button from '../../Button';
import FileUpload from '../../FileUpload';

import { useSnackbar } from 'notistack';
import { DatePicker, Select } from 'antd';

import SearchPostcodeModal from '../../modals/SearchPostcodeModal';
import { errorVariant, infoVariant, successVariant, warningVariant } from '../../../lib/barVariant';
import useAuth from '../../../hooks/useAuth';
import { EmployeeContents } from '../../../pages/campus/staff';
const { Option } = Select;

interface IProps {
  staff: IStaff | undefined;
  housing: IHousing | null;
  housingInput: any;
  housingInputInit: any;
  setHousingInput: (input: any) => void;
  onSetHousingInput: (key: string, value: any) => void;
  setTab: (tab: number) => void;
}

const StaffHousingEditor: FC<IProps> = ({
  staff,
  housing,
  housingInput,
  housingInputInit,
  setHousingInput,
  onSetHousingInput,
  setTab,
}) => {
  const { writeAuthYn } = useAuth();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const dispatch = useDispatch();
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // CA0002 = 사택 구분
  const CA0002 = commonCodes.find(item => item.commonCode === 'CA0002')?.detailCode;
  // SY0007 = 은행
  const SY0007 = commonCodes.find(item => item.commonCode === 'SY0007')?.detailCode;

  const [file, setFile] = useState<File | null>(null);
  const [postcodeVisible, setPostcodeVisible] = useState(false);

  useEffect(() => {
    setFile(null);
  }, []);

  const onCompletePostcode = (data: any) => {
    setHousingInput({
      ...housingInput,
      zipNo: data.zonecode,
      address: data.jibunAddress
    });
    setPostcodeVisible(false);
  }

  const onSubmitHousing = async () => {
    if (!staff) return;
    if (!housingInput.contractBrn) {
      return enqueueSnackbar(t('campus.staff.message6'), infoVariant);
    }

    const form = new FormData();
    if (file) {
      form.append('file', file);
      // 하우징 수정 시 첨부파일 처리
      if (housing) {
        if (housingInput.attachFileCode) {
          form.append('fileCode', housingInput.attachFileCode);
          form.append('fileStatus', 'update');
        } else {
          form.append('fileStatus', 'create');
        }
      }
    } else {
      if (housing && housingInput.attachFileCode) {
        form.append('fileCode', housingInput.attachFileCode);
        if (housingInput.originalName) form.append('fileStatus', 'keep');
        else form.append('fileStatus', 'delete');
      }
    }

    const numberKeys = ['contractSquare', 'totalWrtAmt', 'monthLeaseAmount'];
    const keys = Object.keys(housingInput);
    keys.forEach((key: string) => {
      const value = housingInput[key];
      if (numberKeys.includes(key) && (value === '' || value === null || value === undefined)) {
        form.append(key, '0');
      } else {
        form.append(key, value);
      }
    });

    const res = housing
      ? await dispatch(doUpdateHousing(staff.memberCode, form))
      : await dispatch(doCreateHousing(staff.memberCode, form));

    switch (res?.status) {
      case 200:
        dispatch(doGetHousing(staff.memberCode));
        enqueueSnackbar(housing ? t('common.message.edit1') : t('common.message.create1'), successVariant);
        setHousingInput(housingInputInit);
        setFile(null);
        setTab(EmployeeContents.Housing);
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      default:
        enqueueSnackbar(res?.data?.data || t('common.message.error'), errorVariant);
    }
  }

  return (
    <>
      <div className="comm-bx__between">
        <h3 className='sub-title'>{t('campus.staff.title5')}</h3>
      </div>
      <div className='comm-table__area rowType'>
        <table>
          <colgroup>
            <col width="12%" />
            <col width="12%" />
            <col width="25%" />
            <col width="25%" />
            <col width="25%" />
          </colgroup>
          <tbody>
            <tr>
              <th colSpan={2}>{`* ${t('campus.staff.column13')}`}</th>
              <td className="text-left px-2">
                <div className="w-150p">
                  <Select
                    className="w-100"
                    defaultValue={housingInput.contractBrn}
                    onChange={value => onSetHousingInput('contractBrn', value)}
                  >
                    {
                      CA0002?.map((item: any) => {
                        return (
                          <Option
                            key={item.detailCode}
                            value={item.detailCode}
                          >
                            {item.korName}
                          </Option>
                        )
                      })
                    }
                  </Select>
                </div>
              </td>
              <th>{t('campus.staff.column14')}</th>
              <td className="text-left px-2">
                <div className='flex'>
                  <div className="v-line pl-1">{t('campus.staff.column15')}</div>
                  <div className="w-25">
                    <input
                      name="contractSquare"
                      type="number"
                      className="form-control"
                      value={housingInput.contractSquare}
                      onChange={(e => onSetHousingInput('contractSquare', e.target.value))}
                    />
                  </div>
                  <div className="v-line pl-1">{t('campus.staff.column16')}</div>
                </div>
              </td>
            </tr>
            <tr>
              <th colSpan={2}>{t('campus.staff.column17')}</th>
              <td className="text-left px-2">
                <div className='date-picker__area'>
                  <DatePicker
                    className='w-150'
                    value={housingInput.contractDate ? moment(housingInput.contractDate) : undefined}
                    onChange={e => {
                      if (e === null) onSetHousingInput('contractDate', '');
                      else onSetHousingInput('contractDate', moment(e).format('YYYYMMDD'));
                    }}
                  />
                </div>
              </td>
              <th>{t('campus.staff.column18')}</th>
              <td className="text-left px-2">
                <div className='date-picker__area'>
                  <DatePicker
                    className='w-150'
                    value={housingInput.contractExpireDate ? moment(housingInput.contractExpireDate) : undefined}
                    onChange={e => {
                      if (e === null) onSetHousingInput('contractExpireDate', '');
                      else onSetHousingInput('contractExpireDate', moment(e).format('YYYYMMDD'));
                    }}
                  />
                </div>
              </td>
            </tr>
            <tr>
              <th colSpan={2}>{t('campus.staff.column19')}</th>
              <td className="text-left px-2">
                <div className='flex'>
                  <div className="v-line pl-1">{t('campus.staff.column20')}</div>
                  <div className="w-50">
                    <input
                      type="number"
                      className="form-control"
                      value={housingInput.totalWrtAmt}
                      onChange={(e => onSetHousingInput('totalWrtAmt', e.target.value))}
                    />
                  </div>
                  <div className="v-line pl-1">{t('campus.staff.column21')}</div>
                </div>
              </td>
              <th>{t('campus.staff.column29')}</th>
              <td className="text-left px-2">
                <div className='flex'>
                  <div className="v-line pl-1">{t('campus.staff.column20')}</div>
                  <div className="w-50">
                    <input
                      type="number"
                      className="form-control"
                      value={housingInput.monthLeaseAmount}
                      onChange={(e => onSetHousingInput('monthLeaseAmount', e.target.value))}
                    />
                  </div>
                  <div className="v-line pl-1">{t('campus.staff.column21')}</div>
                </div>
              </td>
            </tr>
            <tr>
              <th rowSpan={2}>{t('common.text.address1')}</th>
              <th>{t('campus.staff.column30')}</th>
              <td className="text-left px-2" colSpan={3}>
                <div className="comm-btn__area">
                  <Button
                    className="btn-basic black small"
                    onClick={() => setPostcodeVisible(true)}
                    text={t('common.button.address')}
                  />
                  <span className='v-line'>
                  {
                    housingInput.zipNo
                      ? `(${housingInput.zipNo}) ${housingInput.address}`
                      : null
                  }
                  </span>
                </div>
              </td>
            </tr>
            <tr>
              <th>{t('campus.staff.column31')}</th>
              <td className="text-left px-2" colSpan={3}>
                <input
                  type="text"
                  className="form-control input-text"
                  value={housingInput.detailAddress}
                  onChange={e => onSetHousingInput('detailAddress', e.target.value)}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="comm-bx__between">
        <h3 className='sub-title'>{t('campus.staff.title6')}</h3>
      </div>
      <div className='comm-table__area rowType edit'>
        <table>
          <colgroup>
            <col width="25%" />
            <col width="25%" />
            <col width="25%" />
            <col width="25%" />
          </colgroup>
          <tbody>
            <tr>
              <th>{t('campus.staff.column23')}</th>
              <td className="text-left px-2">
                <input
                  type="text"
                  className="form-control input-text"
                  value={housingInput.leaseholderName}
                  onChange={e => onSetHousingInput('leaseholderName', e.target.value)}
                />
              </td>
              <th>{t('campus.staff.column24')}</th>
              <td className="text-left px-2">
                <input
                  type="text"
                  className="form-control input-text"
                  value={housingInput.leaseholderTelephone}
                  onChange={e => onSetHousingInput('leaseholderTelephone', e.target.value)}
                />
              </td>
            </tr>
            <tr>
              <th>{t('campus.staff.column25')}</th>
              <td className="text-left px-2" colSpan={3}>
                <div className="flex">
                  <div className="w-150p">
                    <Select
                      className="w-100"
                      defaultValue={housingInput.bankCode}
                      onChange={value => onSetHousingInput('bankCode', value)}
                    >
                      {
                        SY0007?.map((item: any) => {
                          return (
                            <Option
                              key={item.detailCode}
                              value={item.detailCode}
                            >
                              {item.korName}
                            </Option>
                          )
                        })
                      }
                    </Select>
                  </div>
                  <div className="w-100 ml-1">
                    <input
                      type="text"
                      className="form-control input-text"
                      value={housingInput.accountNo}
                      onChange={e => onSetHousingInput('accountNo', e.target.value)}
                    />
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <th>{t('campus.staff.column26')}</th>
              <td className="text-left px-2">
                <input
                  type="text"
                  className="form-control input-text"
                  value={housingInput.brokerName}
                  onChange={e => onSetHousingInput('brokerName', e.target.value)}
                />
              </td>
              <th>{t('campus.staff.column27')}</th>
              <td className="text-left px-2">
                <input
                  type="text"
                  className="form-control input-text"
                  value={housingInput.brokerTelephone}
                  onChange={e => onSetHousingInput('brokerTelephone', e.target.value)}
                />
              </td>
            </tr>
            <tr>
              <th>{t('campus.staff.column28')}</th>
              <td className="text-left px-2 pt-2" colSpan={3}>
                {
                  housingInput.originalName
                    ? (
                      <div>
                        <a
                          className="download-link"
                          href={housingInput.fileLocation}
                          download={housingInput.originalName}
                        >
                          {housingInput.originalName}
                        </a>
                        <span
                          className="ml-1 pointer"
                          onClick={() => onSetHousingInput('originalName', null)}
                        >
                          <CgCloseR />
                        </span>
                      </div>
                    )
                    : null
                }
                <FileUpload
                  file={file}
                  setFile={setFile}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="comm-bx__space">
        <div className="comm-btn__area right">
          <Button
            className='btn-basic pink'
            onClick={onSubmitHousing}
            text={t('common.button.save')}
            writeAuthYn={writeAuthYn}          
          />
          <Button
            className='btn-basic gray'
            onClick={() => {
              setHousingInput(housingInputInit);
              setFile(null);
              setTab(6);
            }}
            text={t('common.button.cancel')}
          />
        </div>
      </div>

      {/* 주소검색 모달 */}
      <SearchPostcodeModal
        postcodeVisible={postcodeVisible}
        setPostcodeVisible={setPostcodeVisible}
        onCompletePostcode={onCompletePostcode}
      />
    </>
  );
}

export default StaffHousingEditor;
